import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';

const LegalLayout = ({ title, updated, path, children }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-black text-gray-700 dark:text-gray-300">
      <Seo title={`${title} — Emboscale`} description={`Emboscale ${title.toLowerCase()}.`} path={path} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <Link to="/" className="text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 text-sm">
          ← Back to emboscale.com
        </Link>
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mt-6 mb-2">{title}</h1>
        <p className="text-sm text-gray-500 mb-10">Last updated: {updated}</p>
        <div className="space-y-8 leading-relaxed [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-gray-900 dark:[&_h2]:text-white [&_h2]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_a]:text-emerald-600 dark:[&_a]:text-emerald-400">
          {children}
        </div>
      </div>
    </div>
  );
};

export const PrivacyPolicy = () => (
  <LegalLayout title="Privacy Policy" updated="July 22, 2026" path="/privacy">
    <section>
      <h2>Who we are</h2>
      <p>
        Emboscale ("we", "us") provides AI agents for hardware engineering. For any
        privacy question or request, contact us at{' '}
        <a href="mailto:contact@emboscale.com">contact@emboscale.com</a>.
      </p>
    </section>

    <section>
      <h2>What we collect</h2>
      <p>
        When you submit the demo request or technical overview form, we collect the
        information you enter: your name, work email, company, and message. We do not
        collect anything else about you, and we do not use analytics or advertising
        trackers on this site.
      </p>
    </section>

    <section>
      <h2>How your data is processed</h2>
      <ul>
        <li>
          This site is hosted by Hostinger. When you visit any page, the web server
          processes your IP address in access logs, as every web server does — we use
          this solely to operate and secure the site (legitimate interest).
        </li>
        <li>
          Fonts and videos are served from our own hosting; no requests go to Google
          or other third-party content networks when you browse.
        </li>
        <li>
          Form submissions are delivered to our inbox via Web3Forms, a form delivery
          service that processes your submission solely to forward it to us.
        </li>
        <li>
          The administrative area of this site (used only by our team) is operated on
          Supabase. Visitor form submissions are not stored there.
        </li>
        <li>We do not sell or share your data with anyone for marketing purposes.</li>
      </ul>
    </section>

    <section>
      <h2>Cookies and local storage</h2>
      <p>
        The public site sets no cookies. If a member of our team signs in to the
        administrative area, a session token is kept in that browser's local storage;
        this never applies to ordinary visitors.
      </p>
    </section>

    <section>
      <h2>Retention</h2>
      <p>
        We keep your contact details for as long as needed to respond to your request
        and conduct any follow-up conversation you have asked for, after which they
        are deleted.
      </p>
    </section>

    <section>
      <h2>Your rights</h2>
      <p>
        You can request access to, correction of, or deletion of the personal data
        you have sent us at any time by emailing{' '}
        <a href="mailto:contact@emboscale.com">contact@emboscale.com</a>. If you are
        in the EU/EEA or UK, you additionally have the rights granted by the GDPR,
        including the right to lodge a complaint with your supervisory authority.
      </p>
    </section>

    <section>
      <h2>Changes</h2>
      <p>
        If we change this policy, we will update this page and the date above.
      </p>
    </section>
  </LegalLayout>
);

export const Terms = () => (
  <LegalLayout title="Terms of Use" updated="July 22, 2026" path="/terms">
    <section>
      <h2>Acceptance</h2>
      <p>
        By using this website you agree to these terms. If you do not agree, please
        do not use the site.
      </p>
    </section>

    <section>
      <h2>Purpose of this site</h2>
      <p>
        This site provides information about Emboscale's products and lets you
        request a demo or technical overview. Product descriptions, timelines, and
        performance figures describe our goals and current capabilities and do not
        constitute a contractual commitment or an offer.
      </p>
    </section>

    <section>
      <h2>Intellectual property</h2>
      <p>
        The content of this site — text, graphics, videos, and branding — belongs to
        Emboscale and may not be reproduced or used commercially without our written
        permission.
      </p>
    </section>

    <section>
      <h2>Acceptable use</h2>
      <p>
        You agree not to misuse the site: no attempts to gain unauthorized access, no
        scraping at disruptive volume, no submitting unlawful or deceptive content
        through our forms.
      </p>
    </section>

    <section>
      <h2>No warranty</h2>
      <p>
        The site is provided "as is" without warranties of any kind. We do not
        guarantee that it will be uninterrupted or error-free.
      </p>
    </section>

    <section>
      <h2>Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, Emboscale is not liable for indirect
        or consequential damages arising from your use of this website. Nothing in
        these terms limits liability that cannot be limited by law.
      </p>
    </section>

    <section>
      <h2>Changes</h2>
      <p>
        We may update these terms; continued use of the site after an update
        constitutes acceptance. Questions:{' '}
        <a href="mailto:contact@emboscale.com">contact@emboscale.com</a>.
      </p>
    </section>
  </LegalLayout>
);
